import { useEffect, useRef, useState } from "react";

import { getEcho } from "./echo";

/** Shared channels carry invalidations only. Customer data comes from authorised REST reads. */
export default function useChatChannel(phoneNumberId, handlers = {}) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const [connected, setConnected] = useState(false);
  const enabled = Boolean(getEcho());

  useEffect(() => {
    const echo = getEcho();
    if (!echo || !phoneNumberId) return undefined;

    const name = `phone-number.${phoneNumberId}`;
    const channel = echo.private(name);

    let timer;
    const bind = (event) =>
      channel.listen(event, () => {
        clearTimeout(timer);
        timer = setTimeout(() => handlersRef.current.onInvalidate?.(), 150);
      });

    bind(".message.created", "onMessageCreated");
    bind(".message.status", "onMessageStatus");
    bind(".conversation.updated", "onConversationUpdated");

    // connection lifecycle + reconnect catch-up
    let everConnected = false;
    const connection = echo.connector?.pusher?.connection;

    const onStateChange = ({ current }) => {
      const up = current === "connected";
      setConnected(up);
      if (up) {
        if (everConnected) handlersRef.current.onReconnect?.();
        everConnected = true;
      }
    };

    if (connection) {
      connection.bind("state_change", onStateChange);
      onStateChange({ current: connection.state });
    }

    return () => {
      clearTimeout(timer);
      channel.stopListening(".message.created");
      channel.stopListening(".message.status");
      channel.stopListening(".conversation.updated");
      connection?.unbind("state_change", onStateChange);
      echo.leave(name);
    };
  }, [phoneNumberId]);

  // Also refresh after permissions change, even if no chat event is sent.
  useEffect(() => {
    if (!phoneNumberId) return undefined;
    const refresh = () => {
      if (document.visibilityState !== "hidden")
        handlersRef.current.onInvalidate?.();
    };
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [phoneNumberId]);

  return { connected, enabled };
}
